import React from "react";
import { Link, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { FiArrowRight } from "react-icons/fi";
import { pageTitle } from "../../helpers/PageTitle";
import ErrorPage from "./ErrorPage";
import { getServiceBySlug } from "../../data/servicesData";

const serviceIcons = {
  "ai-machine-learning": "mdi:brain",
  "web-application-development": "mdi:code-braces",
  "mobile-app-development": "mdi:cellphone",
  "cybersecurity-solutions": "mdi:shield-lock",
  "ui-ux-design": "mdi:palette",
  "seo-organic-growth": "mdi:chart-line",
  "data-analytics": "mdi:chart-bar",
  "devops-cloud-solutions": "mdi:cloud-cog",
  "digital-transformation-consulting": "mdi:lightning-bolt",
  "api-development-integration": "mdi:api",
  "low-code-no-code-development": "mdi:widgets",
  "blockchain-solutions": "mdi:lock-outline",
  "iot-development": "mdi:router-wireless",
  "ar-vr-development": "mdi:virtual-reality",
  "quality-assurance-testing": "mdi:bug-check",
};

const processSteps = [
  {
    num: "01",
    title: "Discovery & Alignment",
    desc: "We analyze your specific requirements, data availability, and system infrastructure to map out a prioritized feature roadmap.",
    details: [
      "Stakeholder interviews & requirement gathering",
      "Technical feasibility analysis & risk assessment",
      "Architecture planning & technology selection",
    ],
    color: "emerald",
  },
  {
    num: "02",
    title: "Iterative Sprints",
    desc: "We write clean, documented code and deploy working iterations every two weeks so you can evaluate progress in real-time.",
    details: [
      "Bi-weekly sprint demos with stakeholder feedback",
      "Clean, modular code with comprehensive documentation",
      "Continuous integration with automated testing pipelines",
    ],
    color: "sky",
  },
  {
    num: "03",
    title: "Continuous QA & Testing",
    desc: "Every release is validated with automated regression testing and manual exploratory testing to ensure bug-free operation.",
    details: [
      "Automated unit, integration & end-to-end test suites",
      "Performance/load testing under real-world conditions",
      "Security penetration testing & vulnerability scanning",
    ],
    color: "violet",
  },
  {
    num: "04",
    title: "Secure Production Launch",
    desc: "We implement automated CI/CD pipelines to deploy your platform securely with zero-downtime and absolute scalability.",
    details: [
      "Zero-downtime deployment with rollback safeguards",
      "Infrastructure-as-code for reproducible environments",
      "Post-launch monitoring, alerting & optimization",
    ],
    color: "amber",
  },
];

export default function ServiceDetailsPage() {
  const { serviceDetailsId } = useParams();
  const service = getServiceBySlug(serviceDetailsId);
  if (!service) return <ErrorPage />;
  const icon = serviceIcons[service.slug] || "mdi:application";

  pageTitle(`${service.title} Details`);

  return (
    <main className="relative overflow-hidden bg-[#fbfdfd]">
      {/* Background decorations */}
      <div className="pointer-events-none absolute left-[-180px] top-[200px] h-[480px] w-[480px] rounded-full bg-teal-100/35 blur-3xl" />
      <div className="pointer-events-none absolute right-[-180px] top-[580px] h-[520px] w-[520px] rounded-full bg-emerald-100/35 blur-3xl" />

      {/* ============ HERO SECTION ============ */}
      <section className="relative z-10 bg-gradient-to-b from-white to-gray-50/60 px-4 pt-[150px] pb-[80px] sm:px-6 max-[991px]:pt-[120px] max-[991px]:pb-[50px]">
        {/* Decorative dots */}
        <div
          className="pointer-events-none absolute left-[2%] top-[35%] h-36 w-52 opacity-50"
          style={{
            backgroundImage: "radial-gradient(#1b667a 1.5px, transparent 1.5px)",
            backgroundSize: "12px 12px",
          }}
        />

        <div className="mx-auto max-w-[1200px]">
          <div className="grid items-center gap-8 lg:grid-cols-[1fr_0.8fr] lg:gap-14">
            {/* Left - Content */}
            <div className="relative z-20">
              {/* Breadcrumb
              <div className="mb-5 flex items-center gap-2 text-[13px] text-gray-400">
                <Link to="/" className="hover:text-[#1b667a] transition-colors">Home</Link>
                <Icon icon="mdi:chevron-right" className="w-3.5 h-3.5" />
                <Link to="/service" className="hover:text-[#1b667a] transition-colors">Services</Link>
                <Icon icon="mdi:chevron-right" className="w-3.5 h-3.5" />
                <span className="text-gray-600 font-medium">{service.title}</span>
              </div> */}

              {/* Label */}
              <div className="mb-16 flex items-center gap-3">
                <div className="w-10 h-[2px] bg-gradient-to-r from-transparent to-[#1b667a]" />
                <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
                  Service Details
                </span>
                <div className="w-10 h-[2px] bg-gradient-to-l from-transparent to-[#1b667a]" />
              </div>

              {/* Heading */}
              <h1 className="text-[clamp(36px,4.5vw,52px)] font-bold leading-[1.1] tracking-[-0.02em] text-gray-900 mb-8">
                {service.title}
              </h1>

              {/* Tagline */}
              <p className="max-w-[560px] text-[16px] leading-[1.8] text-gray-500 mb-15">
                {service.tagline.length > 200
                  ? service.tagline.substring(0, 200) + "..."
                  : service.tagline}
              </p>

              {/* CTA Buttons */}
              <div className="flex items-center gap-3 mt-5 flex-wrap">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-[12px] bg-[#1b667a] px-6 py-3 text-[14px] font-semibold text-white shadow-[0_4px_16px_rgba(27,102,122,0.24)] transition-all duration-300 hover:bg-[#155568] hover:shadow-[0_6px_20px_rgba(27,102,122,0.32)] hover:-translate-y-0.5"
                >
                  Start a Project
                  <FiArrowRight size={16} />
                </Link>
                <Link
                  to="/portfolio"
                  className="inline-flex items-center gap-2 rounded-full border-2 border-gray-300 bg-transparent px-6 py-3 text-[14px] font-semibold text-gray-700 transition-all duration-300 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700"
                >
                  View Projects
                  <Icon icon="mdi:arrow-right" className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative min-h-[350px] lg:min-h-[420px]">
              <div className="absolute -right-4 top-5 h-[88%] w-[92%] rounded-[48px] bg-gradient-to-br from-emerald-100 via-teal-50 to-transparent opacity-80 blur-[1px]" />
              <div
                className="absolute right-0 top-0 h-[380px] w-[95%] overflow-hidden rounded-[36px] shadow-[0_30px_80px_rgba(15,23,42,0.13)] lg:h-[420px]"
                style={{
                  clipPath: "polygon(12% 0%, 100% 0%, 100% 82%, 90% 100%, 0% 100%, 0% 16%)",
                }}
              >
                <img
                  src={service.thumbnailSrc}
                  alt={service.title}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.025]"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#073B40]/10 via-transparent to-[#1b667a]/20" />

                {/* Service badge */}
                <div className="absolute left-5 top-5 rounded-full border border-white/20 bg-black/20 px-3 py-1.5 backdrop-blur-md">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                    <span className="text-[9px] font-bold uppercase tracking-[2px] text-white">
                      {service.title}
                    </span>
                  </div>
                </div>
              </div>

              {/* Floating stat card */}
              <div className="absolute -bottom-4 right-0 z-30 w-[200px] rounded-[22px] bg-[#101827] p-4 shadow-[0_20px_50px_rgba(15,23,42,0.22)] lg:right-[-10px]">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#1b667a] text-white">
                    <Icon icon={icon} className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[14px] font-bold leading-none text-white">
                      Expert-Led
                    </p>
                    <p className="mt-0.5 text-[9px] font-bold text-emerald-400">
                      {service.title}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ OVERVIEW SECTION ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px]">
          <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
            {/* Left - Image */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-[32px] shadow-[0_30px_80px_rgba(15,23,42,0.12)]">
                <img
                  src={service.detailsThumb1}
                  alt={`${service.title} overview`}
                  className="h-[400px] w-full object-cover transition-transform duration-700 hover:scale-[1.03] sm:h-[450px] lg:h-[500px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071D25]/50 via-transparent to-transparent" />

                {/* Overlay Badge */}
                <div className="absolute left-5 top-5 rounded-full border border-white/20 bg-black/25 px-4 py-2 backdrop-blur-md">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.9)]" />
                    <span className="text-[10px] font-bold uppercase tracking-[2.5px] text-white">
                      {service.title}
                    </span>
                  </div>
                </div>

                {/* Bottom Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-[3px] text-emerald-300">
                      Why Choose Us
                    </span>
                  </div>
                  <p className="max-w-[350px] text-[18px] font-bold leading-[1.2] text-white">
                    Engineered solutions that transform your business operations.
                  </p>
                </div>
              </div>

              {/* Floating Stats Card */}
              <div className="absolute -bottom-6 right-[-8px] z-30 w-[180px] rounded-[20px] bg-[#101827] p-5 shadow-[0_25px_60px_rgba(15,23,42,0.25)] sm:right-[-15px] sm:w-[200px]">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#08776F] to-[#0E9F7A] text-white">
                    <Icon icon="mdi:rocket-launch" className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-[22px] font-bold leading-none text-white">
                      98%
                    </p>
                    <p className="mt-1 text-[10px] font-bold text-emerald-400">
                      Client Satisfaction
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <div className="mb-5 flex items-center gap-3">
                <div className="w-10 h-[2px] bg-gradient-to-r from-transparent to-[#1b667a]" />
                <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
                  Overview
                </span>
                <div className="w-10 h-[2px] bg-gradient-to-l from-transparent to-[#1b667a]" />
              </div>

              <h2 className="text-[clamp(28px,3.5vw,40px)] font-bold leading-[1.15] text-gray-900 mb-6">
                {service.title}
              </h2>

              <p className="text-[16px] leading-[1.85] text-gray-500 mb-8">
                {service.overview.length > 280
                  ? service.overview.substring(0, 280) + "..."
                  : service.overview}
              </p>

              {/* Summary Points */}
              <div className="space-y-4">
                {service.keyAdvantages.slice(0, 4).map((adv, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 text-[#1b667a] transition-all duration-300 group-hover:bg-[#1b667a] group-hover:border-[#1b667a] group-hover:scale-110">
                      <Icon icon="mdi:check-circle" className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-[15px] font-bold text-gray-900 mb-1">
                        {adv.title}
                      </h4>
                      <p className="text-[13.5px] leading-[1.65] text-gray-500 line-clamp-2">
                        {adv.subTitle.length > 120
                          ? adv.subTitle.substring(0, 120) + "..."
                          : adv.subTitle}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      </div>

      {/* ============ KEY ADVANTAGES ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-16 lg:py-24 bg-gradient-to-b from-gray-50/80 to-white">
        <div className="mx-auto max-w-[1200px]">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-10 h-[2px] bg-gradient-to-r from-transparent to-[#1b667a]" />
              <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
                Why Partner With Us
              </span>
              <div className="w-10 h-[2px] bg-gradient-to-l from-transparent to-[#1b667a]" />
            </div>
            <div className="w-2 h-2 rounded-full bg-[#1b667a] mx-auto mb-5" />
            <h2 className="text-[clamp(28px,3.5vw,44px)] leading-[1.15] font-bold text-gray-900 mb-5">
              Key Advantages &{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1b667a] via-teal-400 to-green-500">
                Value Delivered
              </span>
            </h2>
            <p className="text-gray-500 max-w-[560px] mx-auto leading-relaxed text-[15px]">
              Discover how our expertise drives measurable results for your business across every dimension.
            </p>
          </div>

          {/* Advantages Grid - 6 items with decorative design */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.keyAdvantages.slice(0, 6).map((adv, index) => {
              const cardStyles = [
                { // Card 1 - Emerald
                  color: "#10B981", borderHover: "hover:border-emerald-300",
                  badgeBg: "from-emerald-50 to-emerald-100/50", badgeText: "text-emerald-600", badgeBorder: "border-emerald-200",
                  numBg: "bg-[#1b667a]", numText: "text-white", numShadow: "shadow-[0_6px_20px_rgba(16,185,129,0.35)]",
                  lineFrom: "from-emerald-400", lineTo: "to-emerald-300",
                },
                { // Card 2 - Sky Blue
                  color: "#0EA5E9", borderHover: "hover:border-sky-300",
                  badgeBg: "from-sky-50 to-sky-100/50", badgeText: "text-sky-600", badgeBorder: "border-sky-200",
                  numBg: "bg-sky-500", numText: "text-white", numShadow: "shadow-[0_6px_20px_rgba(14,165,233,0.35)]",
                  lineFrom: "from-sky-400", lineTo: "to-sky-300",
                },
                { // Card 3 - Violet
                  color: "#8B5CF6", borderHover: "hover:border-violet-300",
                  badgeBg: "from-violet-50 to-violet-100/50", badgeText: "text-violet-600", badgeBorder: "border-violet-200",
                  numBg: "bg-violet-500", numText: "text-white", numShadow: "shadow-[0_6px_20px_rgba(139,92,246,0.35)]",
                  lineFrom: "from-violet-400", lineTo: "to-violet-300",
                },
                { // Card 4 - Amber
                  color: "#F59E0B", borderHover: "hover:border-amber-300",
                  badgeBg: "from-amber-50 to-amber-100/50", badgeText: "text-amber-600", badgeBorder: "border-amber-200",
                  numBg: "bg-amber-500", numText: "text-white", numShadow: "shadow-[0_6px_20px_rgba(245,158,11,0.35)]",
                  lineFrom: "from-amber-400", lineTo: "to-amber-300",
                },
                { // Card 5 - Rose
                  color: "#F43F5E", borderHover: "hover:border-rose-300",
                  badgeBg: "from-rose-50 to-rose-100/50", badgeText: "text-rose-600", badgeBorder: "border-rose-200",
                  numBg: "bg-rose-500", numText: "text-white", numShadow: "shadow-[0_6px_20px_rgba(244,63,94,0.35)]",
                  lineFrom: "from-rose-400", lineTo: "to-rose-300",
                },
                { // Card 6 - Teal
                  color: "#14B8A6", borderHover: "hover:border-teal-300",
                  badgeBg: "from-teal-50 to-teal-100/50", badgeText: "text-teal-600", badgeBorder: "border-teal-200",
                  numBg: "bg-teal-500", numText: "text-white", numShadow: "shadow-[0_6px_20px_rgba(20,184,166,0.35)]",
                  lineFrom: "from-teal-400", lineTo: "to-teal-300",
                },
              ];
              const s = cardStyles[index];

              const patterns = [
                // Card 1 - Emerald dots grid
                <svg key="p1" className="absolute -right-2 -top-2 w-32 h-32 opacity-[0.22]" viewBox="0 0 130 130" fill="none">
                  {[0,1,2,3,4].map(r => [0,1,2,3,4].map(c => <circle key={`${r}${c}`} cx={12+c*28} cy={12+r*28} r="5" fill="#10B981" />))}
                </svg>,
                // Card 2 - Sky rings
                <svg key="p2" className="absolute -bottom-4 -right-4 w-36 h-36 opacity-[0.18]" viewBox="0 0 140 140" fill="none">
                  <circle cx="70" cy="70" r="60" stroke="#0EA5E9" strokeWidth="2.5" strokeDasharray="6 4"/>
                  <circle cx="70" cy="70" r="42" stroke="#0EA5E9" strokeWidth="2"/>
                  <circle cx="70" cy="70" r="24" stroke="#0EA5E9" strokeWidth="1.5" strokeDasharray="3 3"/>
                </svg>,
                // Card 3 - Violet waves
                <svg key="p3" className="absolute bottom-0 left-0 right-0 h-24 opacity-[0.18]" viewBox="0 0 400 90" fill="none" preserveAspectRatio="none">
                  <path d="M0,50 Q50,10 100,50 T200,50 T300,50 T400,50" stroke="#8B5CF6" strokeWidth="3" fill="none"/>
                  <path d="M0,65 Q50,25 100,65 T200,65 T300,65 T400,65" stroke="#8B5CF6" strokeWidth="2" fill="none" opacity="0.5"/>
                </svg>,
                // Card 4 - Amber zigzag
                <svg key="p4" className="absolute -bottom-3 -right-3 w-32 h-32 opacity-[0.20]" viewBox="0 0 130 130" fill="none">
                  <polyline points="10,85 30,35 50,85 70,35 90,85 110,35" stroke="#F59E0B" strokeWidth="3" fill="none" strokeLinecap="round"/>
                  <polyline points="10,105 30,55 50,105 70,55 90,105 110,55" stroke="#F59E0B" strokeWidth="2" fill="none" opacity="0.45" strokeLinecap="round"/>
                </svg>,
                // Card 5 - Rose squares
                <svg key="p5" className="absolute -bottom-3 -right-3 w-32 h-32 opacity-[0.18]" viewBox="0 0 130 130" fill="none">
                  <rect x="6" y="6" width="34" height="34" rx="5" stroke="#F43F5E" strokeWidth="2.5"/>
                  <rect x="50" y="6" width="34" height="34" rx="5" stroke="#F43F5E" strokeWidth="2" strokeDasharray="5 3"/>
                  <rect x="6" y="50" width="34" height="34" rx="5" stroke="#F43F5E" strokeWidth="2" strokeDasharray="5 3"/>
                  <rect x="50" y="50" width="34" height="34" rx="5" stroke="#F43F5E" strokeWidth="2.5"/>
                </svg>,
                // Card 6 - Teal diamonds
                <svg key="p6" className="absolute -right-1 -top-1 w-32 h-32 opacity-[0.18]" viewBox="0 0 130 130" fill="none">
                  <path d="M65 6 L115 55 L65 104 L15 55 Z" stroke="#14B8A6" strokeWidth="2.5" fill="none"/>
                  <path d="M65 22 L95 55 L65 88 L35 55 Z" stroke="#14B8A6" strokeWidth="2" fill="none"/>
                  <path d="M65 38 L78 55 L65 72 L52 55 Z" stroke="#14B8A6" strokeWidth="1.5" fill="none" opacity="0.5"/>
                </svg>,
              ];

              return (
                <div
                  key={index}
                  className={`group relative overflow-hidden rounded-[20px] border border-gray-200 bg-white p-7 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-4 hover:border-gray-100 hover:shadow-[0_25px_60px_rgba(0,0,0,0.18),0_0_40px_rgba(16,185,129,0.12)]`}
                >
                  {/* Decorative background pattern */}
                  {patterns[index]}

                  {/* Left accent border */}
                  <div className={`absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b ${s.lineFrom} ${s.lineTo} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />

                  {/* Top accent bar */}
                  <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${s.lineFrom} via-transparent ${s.lineTo} opacity-0 transition-opacity duration-500 group-hover:opacity-100`} />

                  {/* Corner gradient */}
                  <div
                    className="absolute -top-12 -right-12 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                    style={{ background: `radial-gradient(circle at center, ${s.color}20, transparent 70%)` }}
                  />

                  {/* Number badge */}
                  <div
                    className="relative mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border text-[20px] font-bold transition-all duration-300 group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, ${s.color}0D, ${s.color}1A)`,
                      borderColor: `${s.color}33`,
                      color: s.color,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = s.color;
                      e.currentTarget.style.color = "#fff";
                      e.currentTarget.style.borderColor = s.color;
                      e.currentTarget.style.boxShadow = `0 10px 30px ${s.color}60`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `linear-gradient(135deg, ${s.color}0D, ${s.color}1A)`;
                      e.currentTarget.style.color = s.color;
                      e.currentTarget.style.borderColor = `${s.color}33`;
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  {/* Content */}
                  <h3 className="relative mb-3 text-[19px] font-bold text-gray-900 leading-[1.3]">
                    {adv.title}
                  </h3>
                  <p className="relative text-[14.5px] leading-[1.75] text-gray-500">
                    {adv.subTitle}
                  </p>

                  {/* Bottom accent line */}
                  <div className={`absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r ${s.lineFrom} ${s.lineTo} transition-all duration-500 group-hover:w-full`} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ SHOWCASE BANNER ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-8">
        <div className="mx-auto max-w-[1200px]">
          <div className="relative overflow-hidden rounded-[24px] shadow-[0_20px_60px_rgba(0,0,0,0.1)]">
            <img
              src={service.detailsThumb2}
              alt={`${service.title} showcase`}
              className="w-full h-[350px] sm:h-[420px] lg:h-[480px] object-cover transition-transform duration-700 hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />

            {/* Overlay content */}
            <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-10">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                <span className="text-[10px] font-bold uppercase tracking-[3px] text-emerald-300">
                  Our Expertise
                </span>
              </div>
              <h3 className="text-[clamp(24px,3vw,36px)] font-bold leading-[1.15] text-white max-w-[500px]">
                {service.title} — Engineered for Excellence
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TECH STACK ============ */}
      {service.techStack && service.techStack.length > 0 && (
        <section className="relative z-10 px-4 sm:px-6 py-16 lg:py-24">
          <div className="mx-auto max-w-[1200px]">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="w-10 h-[2px] bg-gradient-to-r from-transparent to-[#1b667a]" />
                <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
                  Tech Stack
                </span>
                <div className="w-10 h-[2px] bg-gradient-to-l from-transparent to-[#1b667a]" />
              </div>
              <div className="w-2 h-2 rounded-full bg-[#1b667a] mx-auto mb-5" />
              <h2 className="text-[clamp(28px,3.5vw,44px)] leading-[1.15] font-bold text-gray-900 mb-5">
                Technologies &{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1b667a] via-teal-400 to-green-500">
                  Frameworks
                </span>
              </h2>
              <p className="text-gray-500 max-w-[560px] mx-auto leading-relaxed text-[15px]">
                We leverage industry-leading tools and modern frameworks to build robust, scalable solutions that stand the test of time.
              </p>
            </div>

            {/* Tech Grid - bigger cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {service.techStack.map((tech, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-[20px] border border-gray-100 bg-white p-7 text-center shadow-[0_4px_20px_rgba(0,0,0,0.04)] transition-all duration-500 hover:-translate-y-2 hover:border-emerald-200 hover:shadow-[0_16px_50px_rgba(0,0,0,0.1)]"
                >
                  {/* Icon */}
                  <div className="mb-4 flex h-16 w-16 mx-auto items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 text-[#1b667a] transition-all duration-300 group-hover:bg-emerald-50 group-hover:border-emerald-300 group-hover:scale-110 group-hover:shadow-[0_8px_25px_rgba(16,185,129,0.15)]">
                    <Icon icon={tech.icon} className="w-8 h-8" />
                  </div>
                  {/* Name */}
                  <h4 className="text-[15px] font-bold text-gray-900 leading-tight">
                    {tech.name}
                  </h4>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ CAPABILITIES & SPECIALIZED SERVICES ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-16 lg:py-20 bg-gray-50/50">
        <div className="mx-auto max-w-[1200px]">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            {/* Core Capabilities */}
            <div>
              <div className="mb-6 flex items-center gap-3">
                <div className="w-8 h-[2px] bg-[#1b667a]" />
                <span className="text-[11px] font-bold tracking-[3px] text-emerald-600 uppercase">
                  Core Capabilities
                </span>
              </div>
              <h2 className="mb-6 text-[clamp(24px,3vw,32px)] font-bold text-gray-900 leading-[1.15]">
                Execution &{" "}
                <span className="text-[#1b667a]">Delivery</span>
              </h2>
              <div className="space-y-3">
                {service.capabilities.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 rounded-xl bg-white p-4 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:border-emerald-100"
                  >
                    <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-50 text-[#1b667a]">
                      <Icon icon="fa6-regular:circle-check" className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] leading-[1.6] text-gray-700 font-medium">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Specialized Services */}
            <div>
              <div className="mb-6 flex items-center gap-3">
                <div className="w-8 h-[2px] bg-teal-500" />
                <span className="text-[11px] font-bold tracking-[3px] text-teal-600 uppercase">
                  Specialized Services
                </span>
              </div>
              <h2 className="mb-6 text-[clamp(24px,3vw,32px)] font-bold text-gray-900 leading-[1.15]">
                Tailored{" "}
                <span className="text-teal-600">Offerings</span>
              </h2>
              <div className="space-y-3">
                {service.specializedServices.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 rounded-xl bg-white p-4 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.02)] transition-all duration-300 hover:shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:border-teal-100"
                  >
                    <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-teal-500" />
                    <span className="text-[14px] leading-[1.6] text-gray-700 font-medium">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT YOU GET ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px]">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Left - Content */}
            <div>
              <div className="mb-5 flex items-center gap-3">
                <div className="w-8 h-[2px] bg-[#1b667a]" />
                <span className="text-[11px] font-bold tracking-[3px] text-emerald-600 uppercase">
                  What You Get
                </span>
              </div>
              <h2 className="mb-4 text-[clamp(28px,3.5vw,42px)] font-bold leading-[1.1] text-gray-900">
                Outcomes from{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#075e68] via-[#087f78] to-[#10a879]">
                  {service.title}
                </span>
              </h2>
              <p className="mb-8 max-w-[500px] text-[15px] leading-[1.8] text-slate-500">
                When you partner with us for {service.title}, we ensure absolute code
                ownership, a scalable database architecture, and complete technical
                support to set you up for long-term success.
              </p>

              {/* Feature list */}
              <div className="space-y-3 mb-8">
                {service.whatYouGet.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3"
                  >
                    <div className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                      <Icon icon="mdi:check" className="w-3 h-3" />
                    </div>
                    <span className="text-[14px] leading-[1.7] text-gray-600">
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 rounded-[12px] bg-[#1b667a] px-6 py-3 text-[14px] font-semibold text-white shadow-[0_4px_16px_rgba(27,102,122,0.24)] transition-all duration-300 hover:bg-[#155568] hover:shadow-[0_6px_20px_rgba(27,102,122,0.32)] hover:-translate-y-0.5"
              >
                Check Live Projects
                <FiArrowRight size={16} />
              </Link>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-[32px] shadow-[0_25px_70px_rgba(15,23,42,0.12)]">
                <img
                  src={service.detailsThumb3}
                  alt={`${service.title} outcomes`}
                  className="h-[350px] w-full object-cover transition-transform duration-700 hover:scale-[1.03] sm:h-[400px] lg:h-[460px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#071D25]/40 via-transparent to-transparent" />

                {/* Top label */}
                <div className="absolute left-5 top-5 rounded-full border border-white/20 bg-black/20 px-3 py-1.5 backdrop-blur-md">
                  <div className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.9)]" />
                    <span className="text-[9px] font-bold uppercase tracking-[2px] text-white">
                      Proven Results
                    </span>
                  </div>
                </div>

                {/* Bottom text */}
                <div className="absolute bottom-5 left-5 right-5">
                  <p className="text-[9px] font-bold uppercase tracking-[3px] text-emerald-300">
                    Measurable Impact
                  </p>
                  <p className="mt-1 max-w-[320px] text-[18px] font-bold leading-[1.15] text-white">
                    Engineering solutions that drive real business growth.
                  </p>
                </div>
              </div>

              {/* Floating stat */}
              <div className="absolute -bottom-5 right-[-8px] z-30 w-[200px] rounded-[18px] bg-gradient-to-br from-[#08776F] to-[#0E9F7A] p-4 shadow-[0_20px_50px_rgba(14,159,122,0.25)] sm:right-[-15px] sm:w-[230px]">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white">
                    <Icon icon="mdi:rocket-launch" className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[18px] font-bold leading-none text-white">
                      Clear
                    </p>
                    <p className="mt-0.5 text-[9px] font-bold text-emerald-100">
                      Delivery Focus
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PROCESS SECTION ============ */}
      <section className="relative z-10 px-4 sm:px-6 py-16 lg:py-24 bg-gradient-to-b from-gray-50/80 to-white">
        <div className="mx-auto max-w-[1200px]">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-10 h-[2px] bg-gradient-to-r from-transparent to-[#1b667a]" />
              <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
                Our Process
              </span>
              <div className="w-10 h-[2px] bg-gradient-to-l from-transparent to-[#1b667a]" />
            </div>
            <div className="w-2 h-2 rounded-full bg-[#1b667a] mx-auto mb-5" />
            <h2 className="text-[clamp(28px,3.5vw,44px)] leading-[1.15] font-bold text-gray-900 mb-5">
              How We Deliver{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1b667a] via-teal-400 to-green-500">
                Success
              </span>
            </h2>
            <p className="text-gray-500 max-w-[600px] mx-auto leading-relaxed text-[14.5px]">
              We align our engineering lifecycle with your business goals, implementing
              structured Agile sprint methodologies to build iteratively.
            </p>
          </div>

          {/* Process Cards - 2 column wider layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {processSteps.map((proc, index) => {
              const colorMap = {
                emerald: { color: "#10B981", bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200" },
                sky: { color: "#0EA5E9", bg: "bg-sky-50", text: "text-sky-600", border: "border-sky-200" },
                violet: { color: "#8B5CF6", bg: "bg-violet-50", text: "text-violet-600", border: "border-violet-200" },
                amber: { color: "#F59E0B", bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200" },
              };
              const c = colorMap[proc.color];

              const decoPatterns = [
                <svg key="d0" className="absolute -right-4 -top-4 w-28 h-28 opacity-[0.08]" viewBox="0 0 120 120" fill="none">
                  {[0,1,2,3].map(r => [0,1,2,3].map(col => <circle key={`${r}${col}`} cx={15+col*30} cy={15+r*30} r="4" fill="#10B981"/>))}
                </svg>,
                <svg key="d1" className="absolute -bottom-4 -right-4 w-28 h-28 opacity-[0.08]" viewBox="0 0 120 120" fill="none">
                  <circle cx="60" cy="60" r="50" stroke="#0EA5E9" strokeWidth="2" strokeDasharray="5 4"/>
                  <circle cx="60" cy="60" r="30" stroke="#0EA5E9" strokeWidth="1.5"/>
                </svg>,
                <svg key="d2" className="absolute -bottom-4 -left-4 w-28 h-28 opacity-[0.08]" viewBox="0 0 120 120" fill="none">
                  <path d="M10,90 Q35,20 60,90 T110,90" stroke="#8B5CF6" strokeWidth="2.5" fill="none"/>
                  <path d="M10,100 Q35,30 60,100 T110,100" stroke="#8B5CF6" strokeWidth="1.5" fill="none" opacity="0.4"/>
                </svg>,
                <svg key="d3" className="absolute -right-4 -bottom-4 w-28 h-28 opacity-[0.08]" viewBox="0 0 120 120" fill="none">
                  <rect x="10" y="10" width="30" height="30" rx="4" stroke="#F59E0B" strokeWidth="2"/>
                  <rect x="50" y="10" width="30" height="30" rx="4" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="4 3"/>
                  <rect x="10" y="50" width="30" height="30" rx="4" stroke="#F59E0B" strokeWidth="1.5" strokeDasharray="4 3"/>
                  <rect x="50" y="50" width="30" height="30" rx="4" stroke="#F59E0B" strokeWidth="2"/>
                </svg>,
              ];

              return (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-[20px] border border-gray-200 bg-white p-8 sm:p-9 shadow-[0_8px_30px_rgba(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-4 hover:border-gray-100 hover:shadow-[0_25px_60px_rgba(0,0,0,0.18),0_0_40px_rgba(16,185,129,0.12)]"
                  style={{ borderColor: undefined }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${c.color}40`;
                    e.currentTarget.style.boxShadow = `0 25px 60px rgba(0,0,0,0.18), 0 0 40px ${c.color}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  {/* Decorative pattern */}
                  {decoPatterns[index]}

                  {/* Top-left colored corner */}
                  <div className="absolute top-0 left-0 w-28 h-28 opacity-[0.06] group-hover:opacity-[0.12] transition-opacity duration-700" style={{ background: `radial-gradient(circle at top left, ${c.color}, transparent 70%)` }} />

                  {/* Left accent bar */}
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `linear-gradient(to bottom, ${c.color}, transparent)` }} />

                  {/* Top accent bar */}
                  <div className="absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `linear-gradient(to right, ${c.color}, transparent)` }} />

                  <div className="relative flex items-start gap-6">
                    {/* Number badge */}
                    <div
                      className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl border text-[22px] font-bold transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `linear-gradient(135deg, ${c.color}12, ${c.color}20)`,
                        borderColor: `${c.color}33`,
                        color: c.color,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = c.color;
                        e.currentTarget.style.color = "#fff";
                        e.currentTarget.style.borderColor = c.color;
                        e.currentTarget.style.boxShadow = `0 10px 30px ${c.color}60`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = `linear-gradient(135deg, ${c.color}12, ${c.color}20)`;
                        e.currentTarget.style.color = c.color;
                        e.currentTarget.style.borderColor = `${c.color}33`;
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      {proc.num}
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <h3 className="mb-2 text-[22px] font-bold text-gray-900 leading-[1.25]">
                        {proc.title}
                      </h3>
                      <p className="mb-4 text-[15px] leading-[1.75] text-gray-500">
                        {proc.desc}
                      </p>

                      {/* Detail bullets */}
                      <ul className="space-y-2">
                        {proc.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: c.color }} />
                            <span className="text-[13.5px] leading-[1.6] text-gray-600">
                              {detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Bottom accent line */}
                  <div className="absolute bottom-0 left-0 h-[3px] w-0 transition-all duration-500 group-hover:w-full" style={{ background: `linear-gradient(to right, ${c.color}, transparent)` }} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section className="relative z-10 px-4 sm:px-6 pb-20">
        <div className="relative mx-auto max-w-[1000px] overflow-hidden rounded-[24px] border border-gray-100 bg-white px-8 py-16 shadow-[0_8px_40px_rgba(0,0,0,0.06)] sm:px-16">
          {/* Decorative elements */}
          <div className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-transparent via-[#1b667a]/20 to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 h-full w-1 bg-gradient-to-b from-transparent via-[#F5A623]/20 to-transparent" />
          <div className="pointer-events-none absolute left-[3%] top-[8%] h-24 w-32 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#1b667a 1px, transparent 1px)", backgroundSize: "10px 10px" }} />
          <div className="pointer-events-none absolute bottom-[8%] right-[3%] h-20 w-28 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#F5A623 1px, transparent 1px)", backgroundSize: "10px 10px" }} />
          <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full border border-[#1b667a]/8" />
          <div className="pointer-events-none absolute -bottom-6 -left-6 h-24 w-24 rounded-full border border-[#F5A623]/8" />

          <div className="relative text-center">
            <div className="inline-flex items-center gap-3 mb-5">
              <div className="w-2 h-2 rounded-full bg-[#1b667a]" />
              <span className="text-[11px] font-bold tracking-[3px] text-[#1b667a] uppercase">
                Let's Connect
              </span>
              <div className="w-2 h-2 rounded-full bg-[#1b667a]" />
            </div>

            <h2 className="mb-5 text-[clamp(28px,4vw,42px)] font-bold leading-[1.15] text-gray-900">
              Is there a specific project or goal{" "}
              <br className="hidden sm:block" />
              <span className="text-[#1b667a]">that you have in mind?</span>
            </h2>

            <p className="mx-auto mb-10 max-w-[520px] text-[16px] leading-[1.8] text-gray-500">
              We engineer secure, scalable and high-performance systems that help your
              business grow without limitations.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 rounded-[15px] bg-[#1b667a] px-8 py-3.5 text-[14px] font-semibold text-white shadow-lg shadow-[#1b667a]/20 transition-all duration-300 hover:bg-[#155568] hover:shadow-xl hover:scale-[1.02]"
              >
                Send Message
                <FiArrowRight className="transition-transform duration-300 group-hover:translate-x-1" size={16} />
              </Link>
              <Link
                to="/service"
                className="inline-flex items-center gap-2 rounded-[15px] border border-gray-200 bg-white px-8 py-3.5 text-[14px] font-semibold text-gray-700 transition-all duration-300 hover:border-gray-300 hover:bg-gray-50"
              >
                View All Services
              </Link>
            </div>

            {/* Bottom features */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 mt-10 border-t border-gray-200/60">
              <div className="flex flex-col items-center text-center group">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-[#1b667a] flex items-center justify-center mb-2.5 transition-colors duration-300">
                  <Icon icon="mdi:shield-check" className="w-5 h-5 text-[#1b667a] group-hover:text-white transition-colors" />
                </div>
                <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">Secure by Design</h4>
                <p className="text-[11px] text-gray-400">Security built in, not added on.</p>
              </div>
              <div className="flex flex-col items-center text-center group">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-[#1b667a] flex items-center justify-center mb-2.5 transition-colors duration-300">
                  <Icon icon="mdi:chart-line" className="w-5 h-5 text-[#1b667a] group-hover:text-white transition-colors" />
                </div>
                <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">Scalable Solutions</h4>
                <p className="text-[11px] text-gray-400">Built to grow with your business.</p>
              </div>
              <div className="flex flex-col items-center text-center group">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-[#1b667a] flex items-center justify-center mb-2.5 transition-colors duration-300">
                  <Icon icon="mdi:server" className="w-5 h-5 text-[#1b667a] group-hover:text-white transition-colors" />
                </div>
                <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">High Availability</h4>
                <p className="text-[11px] text-gray-400">Reliable infrastructure with zero compromise.</p>
              </div>
              <div className="flex flex-col items-center text-center group">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 group-hover:bg-[#1b667a] flex items-center justify-center mb-2.5 transition-colors duration-300">
                  <Icon icon="mdi:rocket-launch" className="w-5 h-5 text-[#1b667a] group-hover:text-white transition-colors" />
                </div>
                <h4 className="text-[13px] font-bold text-gray-900 mb-0.5">Future Ready</h4>
                <p className="text-[11px] text-gray-400">Technology that evolves with your needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
